import { useState } from "react";
import { ChevronRight, Library } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@ncoder/ui";
import { PROMPT_LIBRARY } from "@/constants/multi-agent/promptLibrary.constants";

/**
 * The composer's "Prompt library" picker, in its action row.
 *
 * "Add skill" and "Artifacts" were inert placeholders (nothing backed them) and were removed so no
 * control in the composer is a dead end. "Prompt library" is a real picker over `PROMPT_LIBRARY`: it fills the composer, exactly like a `SAMPLE_PROMPTS` chip
 * already does, just organised into categories.
 */
export function ComposerMenuRow({ onUsePrompt }: { onUsePrompt: (prompt: string) => void }) {
  const [open, setOpen] = useState(false);

  return (
    /* In the composer's own action row since 2026-11-02 (owner: one compact composer, no second row under it). */
    <div className="flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-expanded={open}
            className="inline-flex h-6 items-center gap-1.5 rounded-full bg-muted/70 px-2.5 text-[11.5px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          >
            <Library className="h-3 w-3" />
            Prompt library
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[22rem] p-0" sideOffset={8}>
          <div className="max-h-80 overflow-y-auto p-2">
            {PROMPT_LIBRARY.map((category) => (
              <div key={category.id} className="mb-2 last:mb-0">
                <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {category.label}
                </p>
                <div className="flex flex-col">
                  {category.prompts.map((item) => (
                    <button
                      key={item.title}
                      type="button"
                      onClick={() => {
                        onUsePrompt(item.prompt);
                        setOpen(false);
                      }}
                      title={item.prompt}
                      className="group flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[13px] text-foreground transition-colors hover:bg-muted/60"
                    >
                      <span className="min-w-0 flex-1 truncate">{item.title}</span>
                      <ChevronRight
                        aria-hidden
                        className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                      />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {/*
            Says plainly that this is a static starting-point list, not a saved/editable library —
            §1's rule against implying persistence the backend doesn't have.
          */}
          <div className="border-t border-border px-2.5 py-1.5 text-[11px] text-muted-foreground">
            Starter prompts — pick one to fill the composer, then edit or send it.
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
